import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Luna & Loop "Make a bundle" quiz.
 * Answers live in sessionStorage for this tab only, so a refresh doesn't lose them.
 * Nothing leaves the browser unless the customer signs up at the end.
 */

const STORAGE_KEY = 'll-bundle-builder';

// Extras the made to order builders offer (EXPO option name), and the bundle pieces they
// replace (product title). If they tick one in the pop-up, we drop that piece from the bundle.
const OPTION_EXTRAS = [
  // Moses basket builder: "Luxury Bamboo Mattress"
  { option: /^luxury bamboo mattress$/i, title: /moses basket mattress/i },
  // "Add Luxury Bamboo Mattress?" (Moses bundle-up step), "Add Bamboo …" (changing basket builder)
  { option: /^add\b.*(bamboo|mattress)/i, title: /changing basket mattress/i },
  { option: /liner/i, title: /changing basket liner/i },
  { option: /caddy/i, title: /nappy caddy/i },
  { option: /matching changing basket/i, title: /changing basket(?! mattress| liner)/i },
];

// Runs first inside the colour pop-up. A page loaded from HTML can't change its address,
// so the theme's "update the URL when the variant changes" would throw and stop the picker.
const FRAME_SHIM = `['pushState','replaceState'].forEach(function(m){var o=history[m].bind(history);history[m]=function(s,t){try{return o(s,t)}catch(e){}}});`;
const GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';

const NEEDS = [
  { value: 'sleep', label: 'Sleeping', hint: 'Cosy, safe little places to sleep' },
  { value: 'change', label: 'Changing', hint: 'Making nappy changes a bit lovelier' },
  { value: 'storage', label: 'Nappies and storage', hint: 'Somewhere pretty for all the bits' },
  { value: 'travel', label: 'Out and about', hint: 'Easy changes on the go' },
  { value: 'gift', label: 'A little cuddly gift', hint: 'Soft toys and knits to snuggle' },
];

const STYLES = [
  { value: 'natural', label: 'Natural and calm', hint: 'Soft beiges, creams and toile', swatches: ['#e9dccd', '#f6f0e6', '#c9b49b'] },
  { value: 'pretty', label: 'Soft and pretty', hint: 'Blush pinks, fairies and bows', swatches: ['#f2d6d3', '#f8e8e4', '#e6b9b4'] },
  { value: 'blue', label: 'Classic blue', hint: 'Timeless blue toile and white', swatches: ['#c9d6e3', '#eef2f6', '#8fa6bd'] },
  { value: 'playful', label: 'Playful', hint: 'Teddies, mice and safari friends', swatches: ['#e8d3a9', '#d9c4a3', '#b8c4a1'] },
];

// Most the starting bundle may total for each budget answer, in pence (null = no cap)
const BUDGET_CAPS = { 1: 10000, 2: 30000, 3: null, 4: null };

const BUDGETS = [
  { value: 1, label: 'Up to £100', hint: 'Sweet little essentials' },
  { value: 2, label: '£100 to £300', hint: 'A lovely handmade piece or two' },
  { value: 3, label: '£300 plus', hint: 'The full nursery treat' },
  { value: 4, label: 'Let’s spoil them!', hint: 'Only the very best will do' },
];

const AGES = [
  { value: 'newborn', label: 'Brand new (under 4 weeks)' },
  { value: '1-3', label: '1 to 3 months' },
  { value: '3-6', label: '3 to 6 months' },
  { value: '6+', label: '6 months or older' },
];

const NAME_REACTIONS = [
  (n) => `Ahh, ${n}! What a gorgeous name.`,
  (n) => `${n}! Oh, we love that.`,
  (n) => `Ahh, little ${n}. So cute!`,
];

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const normalise = (value) => String(value).toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();

const cleanName = (value) =>
  String(value || '')
    .replace(/[^\p{L}\p{M}' -]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 30)
    .replace(/(^|[\s'-])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    const dataEl = document.getElementById(`BundleBuilderData-${this.dataset.sectionId}`);
    try {
      this.data = JSON.parse(dataEl?.textContent || '{}');
    } catch {
      this.data = { settings: {}, slots: [] };
    }
    this.settings = this.data.settings || {};
    this.slots = this.data.slots || [];
    this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.settings.currency || 'GBP' });

    this.intro = this.querySelector('[data-bb-intro]');
    this.stage = this.querySelector('[data-bb-stage]');
    this.progress = this.querySelector('.ll-bb__card');

    this.state = this.#load() || this.#fresh();

    this.querySelector('[data-bb-start]')?.addEventListener('click', () => {
      this.state = this.#fresh();
      this.state.step = 1;
      this.#render(true);
    });

    this.stage.addEventListener('click', this.#onClick);
    this.stage.addEventListener('submit', this.#onSubmit);
    this.stage.addEventListener('change', this.#onChange);

    this.#setupPsst();

    if (this.state.step > 0) this.#render(false);
    else this.#updatePsst();
  }

  /* ---------- Sneaky sign up nudge ---------- */

  #setupPsst() {
    this.psst = this.querySelector('[data-bb-psst]');
    this.signupTarget = document.querySelector('.ll-signup');
    if (!this.psst) return;

    // Nothing to point at, or they've just signed up through it
    if (!this.signupTarget || /customer_posted=true/.test(window.location.search)) {
      this.psst.remove();
      this.psst = null;
      return;
    }

    this.psst.addEventListener('click', (event) => {
      event.preventDefault();
      this.signupTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = this.signupTarget.querySelector('input[type="email"]');
      window.setTimeout(() => input?.focus({ preventScroll: true }), 700);
    });
  }

  #updatePsst() {
    if (!this.psst) return;
    const show = this.state.step <= this.#lastStep() && !this.state.signedUp;
    const wasHidden = this.psst.hidden;
    this.psst.hidden = !show;
    if (show && wasHidden) this.#animatePsst();
  }

  #splitInto(el, by) {
    if (!el || el.dataset.split) return [];
    el.dataset.split = 'true';
    const parts = by === 'letters' ? [...el.textContent.trim()] : el.textContent.trim().split(/(\s+)/);
    el.textContent = '';
    return parts
      .filter((part) => part.length)
      .map((part) => {
        const span = document.createElement('span');
        span.textContent = part;
        el.append(span);
        return span;
      });
  }

  async #animatePsst() {
    if (this.psstAnimated || !this.psst) return;
    this.psstAnimated = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const gsap = await this.#loadGsap();
    if (!gsap || !this.psst) return;

    const q = (selector) => this.psst.querySelector(selector);
    const note = q('.ll-bb__psst-note');
    const face = q('.ll-bb__psst-face');
    const winkEye = q('.ll-bb__psst-eye--wink');
    const lid = q('.ll-bb__psst-lid');
    const mouth = q('.ll-bb__psst-mouth');
    const cheeks = this.psst.querySelectorAll('.ll-bb__psst-cheek');
    const letters = this.#splitInto(q('.ll-bb__psst-hi'), 'letters');
    const words = [
      ...this.#splitInto(q('.ll-bb__psst-line'), 'words'),
    ];
    const secret = this.#splitInto(q('.ll-bb__psst-secret'), 'words');
    const stamp = q('.ll-bb__psst-stamp');
    const sparks = [...this.psst.querySelectorAll('.ll-bb__psst-spark')];
    const path = q('.ll-bb__psst-path');
    const tip = q('.ll-bb__psst-tip');
    const arrow = q('.ll-bb__psst-arrow');

    const pathLength = path.getTotalLength();
    const tipLength = tip.getTotalLength();

    // Hide everything first so nothing flashes before the show starts
    gsap.set(note, { opacity: 0, scale: 0.6, rotation: -10, transformOrigin: '20% 50%' });
    gsap.set(face, { scale: 0, rotation: -90, transformOrigin: '50% 50%' });
    gsap.set([...letters, ...words, ...secret], { opacity: 0, y: 14 });
    gsap.set(stamp, { opacity: 0, scale: 3, rotation: -28 });
    // Round line caps leave a dot at zero length, so keep the arrow hidden until it draws
    gsap.set(path, { strokeDasharray: pathLength, strokeDashoffset: pathLength, opacity: 0 });
    gsap.set(tip, { strokeDasharray: tipLength, strokeDashoffset: tipLength, opacity: 0 });

    const burst = () => {
      sparks.forEach((spark, i) => {
        const angle = (i / sparks.length) * Math.PI * 2 + Math.random() * 0.4;
        const distance = 34 + Math.random() * 26;
        gsap.fromTo(
          spark,
          { x: 0, y: 0, scale: 0.4, opacity: 1, rotation: 0 },
          {
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            scale: 1.2 + Math.random(),
            rotation: 180,
            opacity: 0,
            duration: 0.8 + Math.random() * 0.3,
            ease: 'power3.out',
          }
        );
      });
    };

    const wink = () =>
      gsap
        .timeline()
        .to(face, { rotation: -10, duration: 0.25, ease: 'power2.out' })
        .to(winkEye, { scaleY: 0.1, transformOrigin: '50% 50%', duration: 0.12 }, '<')
        .to(lid, { opacity: 1, duration: 0.08 }, '<0.06')
        .to(winkEye, { opacity: 0, duration: 0.05 }, '<')
        .to(mouth, { attr: { d: 'M29 49c7 7 17 6 23-3' }, duration: 0.2 }, '<')
        .to(cheeks, { scale: 1.35, transformOrigin: '50% 50%', duration: 0.25, ease: 'back.out(3)' }, '<')
        .to({}, { duration: 0.45 })
        .to(lid, { opacity: 0, duration: 0.06 })
        .to(winkEye, { opacity: 1, scaleY: 1, duration: 0.15 }, '<')
        .to(mouth, { attr: { d: 'M29 50c6 6.5 16 6.5 22-1' }, duration: 0.25 }, '<')
        .to(cheeks, { scale: 1, duration: 0.3 }, '<')
        .to(face, { rotation: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' }, '<');

    const nudge = () =>
      gsap
        .timeline()
        .to(arrow, { y: 10, duration: 0.18, ease: 'power2.in' })
        .to(arrow, { y: 0, duration: 0.5, ease: 'elastic.out(1.2, 0.35)' })
        .to(arrow, { y: 8, duration: 0.15, ease: 'power2.in' })
        .to(arrow, { y: 0, duration: 0.5, ease: 'elastic.out(1.2, 0.35)' });

    const show = gsap
      .timeline({ paused: true })
      .to(note, { opacity: 1, scale: 1, rotation: -2, duration: 0.7, ease: 'back.out(1.8)' })
      .to(face, { scale: 1, rotation: 0, duration: 0.8, ease: 'elastic.out(1, 0.5)' }, '-=0.45')
      .to(letters, { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: 'back.out(3)' }, '-=0.5')
      .to(words, { opacity: 1, y: 0, duration: 0.35, stagger: 0.04, ease: 'power2.out' }, '-=0.15')
      .to(stamp, { opacity: 1, scale: 1, rotation: -5, duration: 0.45, ease: 'back.out(2.6)' })
      .add(burst, '-=0.18')
      .to(note, { x: 4, duration: 0.05, yoyo: true, repeat: 3, ease: 'none' }, '<')
      .to(secret, { opacity: 1, y: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out' }, '+=0.05')
      .set(path, { opacity: 1 }, '-=0.2')
      .to(path, { strokeDashoffset: 0, duration: 0.9, ease: 'power2.inOut' }, '<')
      .set(tip, { opacity: 1 })
      .to(tip, { strokeDashoffset: 0, duration: 0.25, ease: 'power1.out' })
      .add(wink, '+=0.1')
      .add(() => {
        gsap
          .timeline({ repeat: -1, repeatDelay: 2.8 })
          .add(nudge())
          .add(wink(), '+=0.6')
          .to(stamp, { rotation: 4, duration: 0.12, yoyo: true, repeat: 3, ease: 'sine.inOut' }, '+=0.4')
          .to(stamp, { rotation: -5, duration: 0.2 });
      });

    // Play once it's actually on screen (it sits below the quiz on phones)
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        show.play();
      },
      { threshold: 0.4 }
    );
    observer.observe(this.psst);

    this.psst.addEventListener('pointerenter', burst);
  }

  #loadGsap() {
    if (window.gsap) return Promise.resolve(window.gsap);
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = GSAP_URL;
      script.async = true;
      script.onload = () => resolve(window.gsap || null);
      script.onerror = () => resolve(null);
      document.head.append(script);
    });
  }

  #fresh() {
    return {
      step: 0,
      name: '',
      who: '',
      baby: '',
      gender: '',
      arrived: '',
      due: '',
      age: '',
      needs: [],
      style: '',
      budget: 0,
      picks: null,
      removed: [],
      added: false,
      designed: {},
      covered: {},
      options: {},
      signedUp: false,
    };
  }

  #save() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {}
  }

  #load() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return saved && typeof saved === 'object' ? { ...this.#fresh(), ...saved } : null;
    } catch {
      return null;
    }
  }

  /* ---------- Words ---------- */

  get #isGift() {
    return this.state.who === 'gift';
  }

  #babyName() {
    if (this.state.baby) return this.state.baby;
    return this.#isGift ? 'the little one' : 'your little one';
  }

  #babys() {
    if (this.state.baby) return `${this.state.baby}’s`;
    return this.#isGift ? 'the little one’s' : 'your little one’s';
  }

  #pronoun(kind) {
    const sets = {
      boy: { they: 'he', them: 'him', their: 'his', theyre: 'he’s' },
      girl: { they: 'she', them: 'her', their: 'her', theyre: 'she’s' },
    };
    const fallback = { they: 'they', them: 'them', their: 'their', theyre: 'they’re' };
    return (sets[this.state.gender] || fallback)[kind];
  }

  #little() {
    return this.state.baby ? `little ${this.state.baby}` : this.#isGift ? 'the little one' : 'your little one';
  }

  #fill(text) {
    return String(text || '')
      .replace(/\[babys\]/g, this.#babys())
      .replace(/\[baby\]/g, this.#babyName());
  }

  #dueLabel() {
    if (!this.state.due) return '';
    const [y, m] = this.state.due.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long' });
  }

  /* ---------- Steps ---------- */

  #steps() {
    const s = this.state;
    const hi = s.name ? `, ${escapeHtml(s.name)}` : '';
    const baby = escapeHtml(this.#babyName());
    const babys = escapeHtml(this.#babys());

    return [
      null,
      {
        key: 'name',
        kicker: 'Hello lovely!',
        title: 'First things first, what should we call you?',
        body: this.#textInput('name', 'Your first name', 'given-name'),
        hint: 'Just your first name is perfect. It only stays on this page.',
        skip: 'I’d rather not say',
      },
      {
        key: 'who',
        kicker: s.name ? `Lovely to meet you${hi}!` : 'Lovely to meet you!',
        title: 'Who are we shopping for?',
        body: this.#choices('who', [
          { value: 'own', label: 'My own little one', hint: 'For our nursery' },
          { value: 'gift', label: 'A gift for someone special', hint: 'Baby shower, new arrival or just because' },
        ]),
      },
      {
        key: 'gender',
        kicker: this.#isGift ? 'Ooh, how exciting!' : 'Ahh, how exciting!',
        title: this.#isGift ? 'Is the little one a boy or a girl?' : 'Is your little one a boy or a girl?',
        body: this.#choices('gender', [
          { value: 'boy', label: 'A little boy' },
          { value: 'girl', label: 'A little girl' },
          { value: 'surprise', label: 'It’s a surprise!' },
          { value: 'none', label: 'I’d rather not say' },
        ]),
        hint: 'We only ask so we get our words right. It doesn’t change the colours we pick.',
      },
      {
        key: 'baby',
        kicker: { boy: 'Ahh, a little boy!', girl: 'Ahh, a little girl!', surprise: 'Ooh, a surprise. How lovely!' }[s.gender] || 'Lovely.',
        title:
          { boy: 'And what’s his name?', girl: 'And what’s her name?', surprise: 'Do you have a name in mind yet?' }[s.gender] ||
          (this.#isGift ? 'And what’s the little one called?' : 'And what’s your little one called?'),
        body: this.#textInput('baby', 'Baby’s name or nickname', 'off'),
        hint: 'A name or a cute nickname is perfect. Still deciding? No problem at all.',
        skip: 'Still deciding',
      },
      {
        key: 'arrived',
        kicker: s.baby ? NAME_REACTIONS[s.baby.length % NAME_REACTIONS.length](escapeHtml(s.baby)) : 'Aww, lovely.',
        title: `Has ${baby} arrived yet?`,
        body:
          this.#choices('arrived', [
            { value: 'no', label: 'Not quite yet', hint: 'Counting down the days' },
            { value: 'yes', label: `Yes, ${this.#pronoun('theyre')} here!`, hint: 'Snuggles all round' },
          ]) +
          (s.arrived === 'no'
            ? `<label class="ll-bb__small" for="bb-due">When ${this.#isGift ? 'are they' : 'are you'} due?</label>${this.#dueSelect()}`
            : '') +
          (s.arrived === 'yes'
            ? `<label class="ll-bb__small" for="bb-age">How old is ${baby} now?</label>${this.#ageSelect()}`
            : ''),
        hint: 'Just the month is plenty.',
      },
      {
        key: 'needs',
        kicker: s.arrived === 'no' ? 'Not long now!' : 'The fun bit!',
        title: this.#isGift ? `What do you think they’d love most for ${baby}?` : 'What would make life a little easier right now?',
        body: this.#choices(
          'needs',
          NEEDS.map((n) => ({ ...n, image: this.#needImage(n.value) })),
          true
        ),
        hint: 'Pick as many as you like.',
      },
      {
        key: 'style',
        kicker: 'Ooh, decisions!',
        title: `Which feels most like ${babys} nursery?`,
        body: this.#choices(
          'style',
          STYLES.map((st) => ({ ...st, image: this.#styleImage(st.value) }))
        ),
      },
      {
        key: 'budget',
        kicker: 'Nearly there!',
        title: 'And roughly what would you like to spend?',
        body: this.#choices('budget', BUDGETS),
        hint: 'No pressure, you can swap and remove anything afterwards.',
      },
    ];
  }

  #textInput(key, placeholder, autocomplete) {
    return `<label class="visually-hidden" for="bb-${key}">${escapeHtml(placeholder)}</label>
      <input class="ll-bb__input" id="bb-${key}" name="${key}" type="text" maxlength="30"
        autocomplete="${autocomplete}" placeholder="${escapeHtml(placeholder)}" value="${escapeHtml(this.state[key])}">`;
  }

  #choices(key, options, multiple = false) {
    const current = this.state[key];
    const type = multiple ? 'checkbox' : 'radio';
    const withImages = options.some((o) => o.image);
    return `<fieldset class="ll-bb__choices${withImages ? ' ll-bb__choices--images' : ''}" data-count="${options.length}">
      <legend class="visually-hidden">Choose an option</legend>
      ${options
        .map((o) => {
          const checked = multiple ? current.includes(o.value) : String(current) === String(o.value);
          const swatches = o.swatches
            ? `<span class="ll-bb__swatches" aria-hidden="true">${o.swatches.map((c) => `<span style="background:${c}"></span>`).join('')}</span>`
            : '';
          const image = withImages
            ? `<span class="ll-bb__choice-media">${
                o.image ? `<img src="${escapeHtml(o.image)}" alt="" loading="lazy" width="320" height="240">` : ''
              }</span>`
            : '';
          return `<label class="ll-bb__choice">
            <input type="${type}" name="${key}" value="${escapeHtml(o.value)}"${checked ? ' checked' : ''}>
            ${image}
            ${multiple ? '<span class="ll-bb__tick" aria-hidden="true"></span>' : ''}
            <span class="ll-bb__choice-label">${escapeHtml(o.label)}</span>
            ${o.hint ? `<span class="ll-bb__choice-hint">${escapeHtml(o.hint)}</span>` : ''}
            ${swatches}
          </label>`;
        })
        .join('')}
    </fieldset>`;
  }

  #dueSelect() {
    const now = new Date();
    const months = [];
    for (let i = 0; i < 10; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push(
        `<option value="${value}"${this.state.due === value ? ' selected' : ''}>${d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</option>`
      );
    }
    return `<select class="ll-bb__select" id="bb-due" name="due"><option value="">Choose a month</option>${months.join('')}</select>`;
  }

  #ageSelect() {
    return `<select class="ll-bb__select" id="bb-age" name="age"><option value="">Choose an age</option>${AGES.map(
      (a) => `<option value="${a.value}"${this.state.age === a.value ? ' selected' : ''}>${a.label}</option>`
    ).join('')}</select>`;
  }

  #needImage(need) {
    for (const slot of this.slots) {
      if (slot.need !== need) continue;
      const product = slot.products.find((p) => p.available && p.image);
      if (product) return product.image;
    }
    return '';
  }

  #styleImage(style) {
    const first = (this.settings.styles?.[style] || '').split(',')[0];
    const word = first ? normalise(first) : '';
    if (!word) return '';
    for (const slot of this.slots) {
      if (slot.chooseOnPage) continue;
      const product = slot.products.find((p) => p.image && normalise(p.title).includes(word));
      if (product) return product.image;
    }
    return '';
  }

  #lastStep() {
    return this.#steps().length - 1;
  }

  /* ---------- Rendering ---------- */

  #render(focus) {
    const s = this.state;
    this.intro.hidden = s.step > 0;
    this.stage.hidden = s.step === 0;
    const last = this.#lastStep();
    const progress = s.step > last ? 100 : Math.round(((s.step - 1) / last) * 100);
    this.progress.style.setProperty('--ll-bb-progress', `${progress}%`);
    this.classList.toggle('is-results', s.step > last);
    this.#updatePsst();

    if (s.step === 0) {
      this.intro.querySelector('[data-bb-start]')?.focus();
    } else if (s.step <= last) {
      this.#renderStep(s.step);
    } else {
      this.#renderResults();
    }

    this.#save();
    if (focus) {
      const heading = this.stage.querySelector('.ll-bb__title');
      heading?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #renderStep(index) {
    const step = this.#steps()[index];
    this.stage.innerHTML = `<form class="ll-bb__step" data-step="${step.key}" novalidate>
      <p class="ll-bb__count">Question ${index} of ${this.#lastStep()}</p>
      <p class="ll-bb__kicker">${step.kicker}</p>
      <h2 class="ll-bb__title" tabindex="-1">${step.title}</h2>
      ${step.body}
      <p class="ll-bb__error" data-bb-error hidden></p>
      <div class="ll-bb__nav">
        <button type="submit" class="button ll-bb__next">${index === this.#lastStep() ? 'Show me my bundle' : 'Next'}</button>
        ${step.skip ? `<button type="button" class="ll-bb__link" data-bb-skip>${escapeHtml(step.skip)}</button>` : ''}
        <button type="button" class="ll-bb__link" data-bb-back>Back</button>
      </div>
      ${step.hint ? `<p class="ll-bb__small">${escapeHtml(step.hint)}</p>` : ''}
    </form>`;
  }

  #error(message) {
    const el = this.stage.querySelector('[data-bb-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    const form = input.closest('form');
    if (!form) return;

    if (form.dataset.step === 'arrived' && input.name === 'arrived') {
      this.state.arrived = input.value;
      this.#renderStep(this.state.step);
      this.stage.querySelector('select')?.focus();
      this.#save();
      return;
    }

    if (form.dataset.step === 'result' && input.name.startsWith('opt-')) {
      const rest = input.name.slice(4);
      const slotId = rest.slice(0, rest.lastIndexOf('-'));
      const index = rest.slice(rest.lastIndexOf('-') + 1);
      this.state.options[slotId] = this.state.options[slotId] || [];
      this.state.options[slotId][Number(index)] = input.value;
      this.state.added = false;
      this.#renderResults();
      this.#save();
      return;
    }

    // Single choice questions move on as soon as something is picked
    if (input.type === 'radio' && ['who', 'gender', 'style', 'budget'].includes(form.dataset.step)) {
      window.setTimeout(() => form.requestSubmit(), 180);
    }
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button, a') : null;
    if (!target) return;

    if (target.matches('[data-bb-back]')) {
      this.state.step = Math.max(0, this.state.step - 1);
      this.#render(true);
    } else if (target.matches('[data-bb-skip]')) {
      const key = this.stage.querySelector('form')?.dataset.step;
      if (key) this.state[key] = '';
      this.state.step += 1;
      this.#render(true);
    } else if (target.matches('[data-bb-swap]')) {
      this.#swap(target.dataset.bbSwap);
    } else if (target.matches('[data-bb-remove]')) {
      this.state.removed.push(target.dataset.bbRemove);
      this.state.added = false;
      this.#renderResults();
      this.#save();
    } else if (target.matches('[data-bb-restore]')) {
      this.state.removed = [];
      this.state.added = false;
      this.#renderResults();
      this.#save();
    } else if (target.matches('[data-bb-add]')) {
      this.#addToCart();
    } else if (target.matches('[data-bb-design]')) {
      const item = this.#bundle().find((i) => i.slot.id === target.dataset.bbDesign);
      if (item) this.#openDesigner(item);
    } else if (target.matches('[data-bb-restart]')) {
      this.state = this.#fresh();
      this.state.step = 1;
      this.#render(true);
    }
  };

  #onSubmit = (event) => {
    event.preventDefault();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const key = form.dataset.step;
    const data = new FormData(form);

    if (key === 'signup') {
      this.#signup(form);
      return;
    }

    if (key === 'name' || key === 'baby') {
      this.state[key] = cleanName(data.get(key));
    } else if (key === 'needs') {
      const needs = data.getAll('needs').map(String);
      if (!needs.length) return this.#error('Pick at least one and we’ll take it from there.');
      this.state.needs = needs;
    } else if (key === 'arrived') {
      const arrived = String(data.get('arrived') || '');
      if (!arrived) return this.#error('Just let us know and we’ll carry on.');
      this.state.arrived = arrived;
      this.state.due = arrived === 'no' ? String(data.get('due') || '') : '';
      this.state.age = arrived === 'yes' ? String(data.get('age') || '') : '';
    } else if (key === 'budget') {
      const budget = Number(data.get('budget'));
      if (!budget) return this.#error('Pick whichever feels about right.');
      this.state.budget = budget;
    } else {
      const value = String(data.get(key) || '');
      if (!value) return this.#error('Pick whichever feels right.');
      this.state[key] = value;
    }

    this.state.step += 1;
    if (this.state.step > this.#lastStep()) {
      this.state.picks = null;
      this.state.removed = [];
      this.state.added = false;
      this.#showLoading();
      return;
    }
    this.#render(true);
  };

  /**
   * The "building your bundle" moment: a little crochet basket, the bundle's own
   * products dropping in, and the baby's name. GSAP when it's available, a calm
   * static version when it isn't or they prefer less motion.
   */
  async #showLoading() {
    this.progress.style.setProperty('--ll-bb-progress', '100%');
    this.classList.add('is-results');

    const picks = this.#bundle().slice(0, 4);
    const name = this.state.baby ? `${this.#babys()} bundle` : 'Your little bundle';
    const babys = escapeHtml(this.#babys());
    const lines = [
      `Picking out ${babys} favourites…`,
      'Choosing the cosiest bits…',
      'Adding a sprinkle of love…',
    ];
    const letters = [...name]
      .map((c) => (c === ' ' ? '<span class="ll-bb__build-space"> </span>' : `<span class="ll-bb__build-letter">${escapeHtml(c)}</span>`))
      .join('');
    const items = picks
      .map(
        (i, n) =>
          `<span class="ll-bb__build-item" style="--n:${n}">${
            i.product.image ? `<img src="${escapeHtml(i.product.image)}" alt="" width="120" height="120">` : '<span>♡</span>'
          }</span>`
      )
      .join('');
    const sparks = Array.from({ length: 10 }, () => '<span class="ll-bb__build-spark"></span>').join('');
    const hearts = Array.from({ length: 3 }, () => '<span class="ll-bb__build-heart">♡</span>').join('');

    // A full screen pop-up, so it's in view wherever they've scrolled to on a phone
    this.querySelector('.ll-bb__overlay')?.remove();
    const overlay = document.createElement('div');
    overlay.className = 'll-bb__overlay';
    overlay.innerHTML = `<div class="ll-bb__build" data-count="${picks.length}" role="status">
      <p class="ll-bb__kicker">Just a moment</p>
      <h2 class="ll-bb__build-name" tabindex="-1" aria-label="${escapeHtml(name)}"><span aria-hidden="true">${letters}</span></h2>
      <div class="ll-bb__build-stage" aria-hidden="true">
        <svg class="ll-bb__build-back" viewBox="0 0 260 200">
          <ellipse cx="130" cy="112" rx="108" ry="18" fill="#efe4d8" stroke="#c4a089" stroke-width="3"/>
        </svg>
        <div class="ll-bb__build-items">${items}</div>
        <svg class="ll-bb__build-front" viewBox="0 0 260 200">
          <path d="M22 112A108 18 0 0 0 238 112C238 168 200 192 130 192C60 192 22 168 22 112Z" fill="#dcc8b3" stroke="#c4a089" stroke-width="3" stroke-linejoin="round"/>
          <path class="ll-bb__build-row" d="M30 126Q130 158 230 126" />
          <path class="ll-bb__build-row" d="M36 146Q130 176 224 146" />
          <path class="ll-bb__build-row" d="M52 166Q130 190 208 166" />
          <path class="ll-bb__build-rim" d="M22 112A108 18 0 0 0 238 112" />
          <g class="ll-bb__build-tassel">
            <path d="M126 134L119 166M134 134L141 166" stroke="#fbf8f5" stroke-width="7" stroke-linecap="round"/>
            <circle cx="130" cy="132" r="6" fill="#fbf8f5"/>
          </g>
        </svg>
        <div class="ll-bb__build-sparks">${sparks}${hearts}</div>
      </div>
      <p class="ll-bb__build-status" aria-live="polite">${lines[0]}</p>
    </div>`;
    this.append(overlay);
    document.documentElement.classList.add('ll-bb-locked');

    const root = overlay.querySelector('.ll-bb__build');
    root.querySelector('.ll-bb__build-name')?.focus({ preventScroll: true });

    const done = () => {
      if (!overlay.isConnected) return;
      // Results go in behind the pop-up (scrolled to the top), then it fades away
      if (this.state.step > this.#lastStep()) this.#render(true);
      document.documentElement.classList.remove('ll-bb-locked');
      overlay.classList.add('is-leaving');
      window.setTimeout(() => overlay.remove(), 400);
    };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const gsap = reduce ? null : await Promise.race([this.#loadGsap(), new Promise((r) => window.setTimeout(() => r(null), 1500))]);
    if (!overlay.isConnected) return;
    if (!gsap) {
      root.classList.add('is-static');
      window.setTimeout(done, reduce ? 600 : 1300);
      return;
    }

    const q = (selector) => root.querySelectorAll(selector);
    const status = root.querySelector('.ll-bb__build-status');
    const basket = root.querySelectorAll('.ll-bb__build-back, .ll-bb__build-front');
    const setStatus = (text) =>
      gsap.timeline()
        .to(status, { autoAlpha: 0, y: -6, duration: 0.2 })
        .add(() => (status.textContent = text))
        .to(status, { autoAlpha: 1, y: 0, duration: 0.3 });

    root.classList.add('is-animated');
    const tl = gsap.timeline({ onComplete: done });
    tl.from(basket, { scale: 0.4, y: 40, autoAlpha: 0, duration: 0.8, ease: 'elastic.out(1, 0.55)', transformOrigin: '50% 80%' })
      .from(q('.ll-bb__build-row'), { autoAlpha: 0, scaleX: 0, transformOrigin: '50% 50%', duration: 0.4, stagger: 0.1, ease: 'power2.out' }, '-=0.45')
      .from(q('.ll-bb__build-tassel'), { autoAlpha: 0, y: -10, rotation: -20, transformOrigin: '50% 0%', duration: 0.5, ease: 'back.out(3)' }, '-=0.2')
      .from(q('.ll-bb__build-letter'), { autoAlpha: 0, y: 28, rotation: () => gsap.utils.random(-25, 25), duration: 0.5, stagger: 0.035, ease: 'back.out(2.5)' }, 0.15)
      .from(status, { autoAlpha: 0, y: 8, duration: 0.4 }, 0.5);

    q('.ll-bb__build-item').forEach((item, n) => {
      const at = 0.9 + n * 0.3;
      tl.fromTo(
        item,
        { y: -190, autoAlpha: 0, rotation: gsap.utils.random(-50, 50), scale: 0.7 },
        { y: 0, autoAlpha: 1, rotation: gsap.utils.random(-10, 10), scale: 1, duration: 0.75, ease: 'bounce.out' },
        at
      ).to(basket, { scaleY: 0.94, scaleX: 1.03, transformOrigin: '50% 100%', duration: 0.09, yoyo: true, repeat: 1, ease: 'power1.inOut' }, at + 0.32);
    });

    const settle = Math.max(2, 0.9 + picks.length * 0.3 + 0.5);
    tl.add(() => setStatus(lines[1]), 1.3)
      .add(() => setStatus(lines[2]), settle - 0.6)
      .to(q('.ll-bb__build-letter'), { y: -8, duration: 0.22, stagger: { each: 0.03, yoyo: true, repeat: 1 }, ease: 'sine.inOut' }, settle - 0.3)
      .to(basket, { rotation: 3, duration: 0.12, yoyo: true, repeat: 3, transformOrigin: '50% 100%', ease: 'sine.inOut' }, settle)
      .fromTo(
        q('.ll-bb__build-spark'),
        { x: 0, y: 0, scale: 0, autoAlpha: 1 },
        {
          x: () => gsap.utils.random(-140, 140),
          y: () => gsap.utils.random(-130, -20),
          scale: () => gsap.utils.random(0.6, 1.3),
          autoAlpha: 0,
          duration: 0.9,
          ease: 'power3.out',
        },
        settle
      )
      .fromTo(
        q('.ll-bb__build-heart'),
        { x: (i) => (i - 1) * 46, y: 0, scale: 0.4, autoAlpha: 0 },
        { y: -110, scale: 1.2, autoAlpha: 1, duration: 1, stagger: 0.12, ease: 'power2.out' },
        settle
      )
      .to(q('.ll-bb__build-heart'), { autoAlpha: 0, duration: 0.3 }, settle + 0.8)
      .to({}, { duration: 0.15 });
  }

  /* ---------- Picking products ---------- */

  #styleWords() {
    const list = this.settings.styles?.[this.state.style] || '';
    return list
      .split(',')
      .map((w) => normalise(w))
      .filter(Boolean);
  }

  #eligibleSlots() {
    const s = this.state;
    const olderBaby = s.arrived === 'yes' && s.age === '6+';
    const budget = s.budget || 2;
    const needs = NEEDS.map((n) => n.value).filter((n) => s.needs.includes(n));

    const slots = [];
    for (const need of needs) {
      for (const slot of this.slots) {
        if (slot.need !== need) continue;
        if (budget < slot.budgetMin || budget > slot.budgetMax) continue;
        if (slot.stage === 'newborn' && olderBaby) continue;
        if (!slot.products.some((p) => p.available)) continue;
        slots.push(slot);
      }
    }

    // Priority order keeps a spread of needs: the first slot of each need, then the rest
    const firsts = needs.map((n) => slots.find((sl) => sl.need === n)).filter(Boolean);
    const rest = slots.filter((sl) => !firsts.includes(sl));
    return [...firsts, ...rest];
  }

  #rank(slot) {
    const words = this.#styleWords();
    const available = slot.products.filter((p) => p.available);
    const score = (p) => {
      const title = normalise(p.title);
      const i = words.findIndex((w) => title.includes(w));
      return i === -1 ? words.length : i;
    };
    return available
      .map((p, i) => ({ p, i }))
      .sort((a, b) => score(a.p) - score(b.p) || a.i - b.i)
      .map(({ p }) => p);
  }

  #ensurePicks() {
    if (this.state.picks) return;
    this.state.picks = {};
    // Fill in priority order, keeping the starting total inside the budget they picked.
    // Within a slot, take the best style match that still fits, or leave the slot out.
    const cap = BUDGET_CAPS[this.state.budget || 2] ?? Infinity;
    const max = this.settings.maxItems || 6;
    const picked = new Set();
    let total = 0;
    for (const slot of this.#eligibleSlots()) {
      if (picked.size >= max) break;
      // Leave out things an earlier pick already offers as an option (the mattress in the Moses basket builder)
      if (slot.skipWith?.some((id) => picked.has(id))) continue;
      if (slot.needsWith?.length && !slot.needsWith.some((id) => picked.has(id))) continue;
      const pick = this.#rank(slot).find((p) => total + p.priceMin <= cap);
      if (!pick) continue;
      this.state.picks[slot.id] = pick.id;
      picked.add(pick.id);
      total += pick.priceMin;
    }
  }

  #swap(slotId) {
    const slot = this.slots.find((sl) => sl.id === slotId);
    if (!slot) return;
    const ranked = this.#rank(slot);
    const current = ranked.findIndex((p) => p.id === this.state.picks[slotId]);
    const next = ranked[(current + 1) % ranked.length];
    this.state.picks[slotId] = next.id;
    this.state.added = false;
    delete this.state.options[slotId];
    this.#renderResults();
    this.#save();
    this.stage.querySelector(`[data-bb-item="${slotId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #selectedVariant(slot, product) {
    const chosen = this.state.options[slot.id] || [];
    if (product.variants.length <= 1) return product.variants[0];
    if (!chosen.length) return this.#sizeForAge(product) || product.variants.find((v) => v.id === product.defaultVariant) || product.variants[0];
    const match = product.variants.find((v) => v.options.every((o, i) => (chosen[i] ?? product.variants[0].options[i]) === o));
    return match || product.variants.find((v) => v.id === product.defaultVariant) || product.variants[0];
  }

  /** Clothes start in the size that fits now: newborn before birth, then by age */
  #sizeForAge(product) {
    const index = product.options.findIndex((name) => /size/i.test(name));
    if (index === -1) return null;
    const s = this.state;
    const age = s.arrived === 'yes' ? s.age : 'newborn';
    const want = { newborn: /newborn|^0-1/i, '1-3': /0-3/i, '3-6': /3-6/i, '6+': /6-12|6-9/i }[age];
    return want ? product.variants.find((v) => v.available !== false && want.test(v.options[index])) : null;
  }

  #bundle() {
    this.#ensurePicks();
    const items = [];
    for (const slot of this.slots) {
      const productId = this.state.picks[slot.id];
      if (!productId || this.state.removed.includes(slot.id)) continue;
      const product = slot.products.find((p) => p.id === productId);
      if (!product) continue;
      const variant = this.#selectedVariant(slot, product);
      items.push({ slot, product, variant, alternatives: this.#rank(slot).length });
    }
    const ids = new Set(items.map((i) => i.product.id));
    return items.filter(
      (i) =>
        !i.slot.skipWith?.some((id) => ids.has(id)) &&
        !(i.slot.needsWith?.length && !i.slot.needsWith.some((id) => ids.has(id))) &&
        !this.state.covered?.[i.slot.id]
    );
  }

  /** Made to order pieces get the colour pop-up; ready made one-offs in the same slot just go in the basket */
  #popup({ slot, product }) {
    return Boolean(slot.chooseOnPage && product.madeToOrder !== false);
  }

  #isDesigned(item) {
    return this.state.designed?.[item.slot.id] === item.product.id;
  }

  /** Products left out of the bundle because this one offers them as an option */
  #optionExtras(product) {
    return this.slots
      .filter((sl) => sl.skipWith?.includes(product.id) && this.state.needs.includes(sl.need))
      .map((sl) => this.#rank(sl)[0]?.title)
      .filter(Boolean);
  }

  /* ---------- Results ---------- */

  #resultHeading() {
    const s = this.state;
    const name = escapeHtml(s.name);
    const little = escapeHtml(this.#little());
    if (this.#isGift) {
      return name
        ? `What a lovely gift, ${name}! We think ${little} is going to adore this.`
        : `What a lovely gift! We think ${little} is going to adore this.`;
    }
    if (name && s.baby) return `Thanks ${name}, we think you and ${little} would love this!`;
    if (name) return `Thanks ${name}, we think you and your little one would love this!`;
    if (s.baby) return `We think you and ${little} would love this!`;
    return 'Here’s the bundle we’d put together for you!';
  }

  #resultLede() {
    const s = this.state;
    const baby = escapeHtml(this.#babyName());
    if (this.#isGift) return 'Every piece is chosen with love, and the handmade bits are made just for them in Somerset.';
    if (s.arrived === 'no') {
      const month = this.#dueLabel();
      return month
        ? `Not long now! Here’s everything to have ready before ${baby} arrives in ${month}.`
        : `Not long now! Here’s everything to have ready before ${baby} arrives.`;
    }
    return `Everything here is picked to make these early days with ${escapeHtml(this.#pronoun('them'))} a little calmer (and a lot cuter).`;
  }

  #renderResults() {
    const items = this.#bundle();
    const addable = items.filter((i) => !this.#popup(i));
    const onPage = items.filter((i) => this.#popup(i));
    const pending = onPage.filter((i) => !this.#isDesigned(i));
    const total = items.reduce((sum, i) => sum + (i.variant?.price ?? i.product.priceMin), 0);
    const babys = escapeHtml(this.#babys());
    const bundleName = this.state.baby ? `${babys} bundle` : 'Your bundle';
    const restToAdd = addable.length && !this.state.added;
    let addLabel = this.state.baby ? `Add ${babys} bundle to basket` : 'Add my bundle to basket';
    if (pending.length && !restToAdd) addLabel = 'Choose colours';
    const allIn = !pending.length && (this.state.added || !addable.length);
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const addButton = (extra = '') => {
      if (!items.length) return '';
      if (allIn) return `<a class="button ll-bb__add${extra}" href="${cartUrl}">It’s in your basket ♡ View basket</a>`;
      return `<button type="button" class="button ll-bb__add${extra}" data-bb-add>${addLabel}</button>`;
    };

    const onPageNote = pending.length
      ? `<p class="ll-bb__small">${
          pending.length > 1 ? 'The handmade pieces are' : 'The handmade piece is'
        } made to order, so first you’ll choose colours in a quick pop-up${
          restToAdd ? '. Then everything goes in your basket together.' : '.'
        }</p>`
      : '';

    const bar =
      items.length
        ? `<div class="ll-bb__bar"><span><small>${bundleName}</small><strong>${this.#format(total)}</strong></span>${addButton(' ll-bb__add--bar')}</div>`
        : '';

    const body = items.length
      ? `<div class="ll-bb__results">
          <ul class="ll-bb__items">${items.map((i) => this.#itemHtml(i)).join('')}</ul>
          <aside class="ll-bb__summary" aria-label="${bundleName} summary">
            <p class="ll-bb__summary-title">${bundleName}</p>
            <ul class="ll-bb__summary-list">
              ${items
                .map(
                  (i) => `<li><span>${escapeHtml(i.product.title)}${
                    this.#isDesigned(i) ? ' <em class="ll-bb__in">✓ in basket</em>' : ''
                  }</span><span>${this.#popup(i) ? 'from ' : ''}${this.#format(
                    this.#popup(i) ? i.product.priceMin : i.variant?.price ?? i.product.priceMin
                  )}</span></li>`
                )
                .join('')}
              ${this.#coveredRows()}
            </ul>
            <p class="ll-bb__total"><span>Bundle total</span><strong>${onPage.length ? 'from ' : ''}${this.#format(total)}</strong></p>
            ${addButton()}
            <p class="ll-bb__error" data-bb-error hidden role="alert"></p>
            ${onPageNote}
            <ul class="ll-bb__perks">
              <li>Handmade with love in Somerset</li>
              <li>Swap or remove anything you like</li>
            </ul>
            <div class="ll-bb__nav ll-bb__nav--quiet">
              ${this.state.removed.length ? '<button type="button" class="ll-bb__link" data-bb-restore>Bring back removed items</button>' : ''}
              <button type="button" class="ll-bb__link" data-bb-restart>Start again</button>
            </div>
          </aside>
        </div>
`
      : `<p class="ll-bb__empty">Oh no, we couldn’t find a match for that. Try picking a few more things you need, or a different budget.</p>
        <div class="ll-bb__nav"><button type="button" class="button" data-bb-restart>Start again</button></div>`;

    this.stage.innerHTML = `<form class="ll-bb__step ll-bb__step--result" data-step="result" novalidate>
      <p class="ll-bb__kicker">Your little bundle</p>
      <h2 class="ll-bb__title" tabindex="-1">${this.#resultHeading()}</h2>
      <p class="ll-bb__lede">${this.#resultLede()}</p>
      ${body}
    </form>
    ${this.#signupHtml()}
    ${bar}`;
  }

  /** Pieces they chose as extras inside a builder, shown so nothing seems to vanish */
  #coveredRows() {
    return Object.entries(this.state.covered || {})
      .map(([slotId, by]) => {
        const slot = this.slots.find((sl) => sl.id === slotId);
        const product = slot?.products.find((p) => p.id === this.state.picks?.[slotId]);
        if (!product) return '';
        return `<li class="ll-bb__covered"><span>${escapeHtml(product.title)} <em class="ll-bb__in">✓ added with your ${escapeHtml(
          by.replace(/\bcrochet\s+/i, '').toLowerCase()
        )}</em></span><span></span></li>`;
      })
      .join('');
  }

  #itemHtml({ slot, product, variant, alternatives }) {
    const reason = slot.reason ? `<p class="ll-bb__item-reason">${escapeHtml(this.#fill(slot.reason))}</p>` : '';
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.imageAlt)}" width="480" height="480" loading="lazy">`
      : '';

    const popup = this.#popup({ slot, product });
    let options = '';
    if (!popup && product.variants.length > 1) {
      options = `<div class="ll-bb__item-options">${product.options
        .map((name, i) => {
          const values = [...new Set(product.variants.map((v) => v.options[i]))];
          return `<label><span class="visually-hidden">${escapeHtml(name)}</span>
            <select name="opt-${slot.id}-${i}" aria-label="${escapeHtml(name)}">
              ${values.map((v) => `<option${variant.options[i] === v ? ' selected' : ''}>${escapeHtml(v)}</option>`).join('')}
            </select></label>`;
        })
        .join('')}</div>`;
    }

    const extras = popup ? this.#optionExtras(product) : [];
    const extrasNote = extras.length
      ? `<p class="ll-bb__item-extra">♡ You can add the ${escapeHtml(extras.join(' and '))} as you choose colours, so we haven’t doubled up.</p>`
      : '';
    const designed = this.#isDesigned({ slot, product });

    const price = popup
      ? `From ${this.#format(product.priceMin)}`
      : this.#format(variant?.price ?? product.priceMin);

    return `<li class="ll-bb__item" data-bb-item="${slot.id}">
      <a class="ll-bb__item-media" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
        ${image}
        ${
          popup
            ? '<span class="ll-bb__badge">Handmade to order</span>'
            : slot.chooseOnPage
              ? '<span class="ll-bb__badge">Ready to send</span>'
              : ''
        }
      </a>
      <div class="ll-bb__item-body">
        <p class="ll-bb__item-title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></p>
        ${reason}
        ${extrasNote}
        ${options}
        <p class="ll-bb__item-price">${price}</p>
        <div class="ll-bb__item-actions">
          ${
            popup
              ? designed
                ? '<span class="ll-bb__item-note">In your basket with your colours ♡</span>'
                : `<button type="button" class="ll-bb__pill ll-bb__pill--solid" data-bb-design="${slot.id}">Choose colours<span class="visually-hidden"> for ${escapeHtml(product.title)}</span></button>`
              : ''
          }
          ${alternatives > 1 ? `<button type="button" class="ll-bb__pill" data-bb-swap="${slot.id}">Show me another<span class="visually-hidden"> instead of ${escapeHtml(product.title)}</span></button>` : ''}
          <button type="button" class="ll-bb__pill ll-bb__pill--ghost" data-bb-remove="${slot.id}">Remove<span class="visually-hidden"> ${escapeHtml(product.title)}</span></button>
        </div>
      </div>
    </li>`;
  }

  #format(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: this.settings.currency || 'GBP',
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  }

  /* ---------- Basket ---------- */

  async #addToCart() {
    if (this.busy) return;
    const toAdd = () =>
      this.state.added
        ? []
        : this.#bundle()
            .filter((i) => !this.#popup(i) && i.variant?.available !== false)
            .map((i) => ({ id: i.variant.id, quantity: 1, properties: { _bundle: 'Make a bundle' } }));
    const nextPending = () => this.#bundle().filter((i) => this.#popup(i) && !this.#isDesigned(i));

    // Handmade pieces first, one pop-up at a time, so nothing goes in the basket half finished.
    // Re-check each time, as extras picked in one builder can replace pieces in the bundle.
    let pending = nextPending();
    const designedAny = pending.length > 0;
    for (let step = 1; pending.length; step++) {
      const done = await this.#openDesigner(pending[0], { step, total: step - 1 + pending.length, rest: toAdd().length > 0 });
      if (!done) return;
      pending = nextPending();
    }

    const items = toAdd();

    if (!items.length) {
      this.state.added = true;
      this.#save();
      this.#renderResults();
      if (designedAny) this.#refreshCart('add');
      return;
    }

    const buttons = [...this.querySelectorAll('[data-bb-add]')];
    const setText = (text) => buttons.forEach((b) => (b.textContent = text));
    const original = buttons[0]?.textContent || '';
    this.busy = true;
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });
    setText('Popping it in your basket…');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const routes = window.Theme?.routes || {};
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds().join(',') }),
      });
      const result = await response.json();
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bundle-builder',
          sourceId: this.dataset.sectionId,
          itemCount: items.length,
          sections: result.sections,
          didError: Boolean(result.status),
        },
      });

      if (result.status) {
        this.#error(
          'Sorry, we couldn’t add everything just now. Some pieces might have just sold out, so try swapping them or refresh and try again.'
        );
        setText(original);
      } else {
        this.state.added = true;
        this.#save();
        this.#renderResults();
      }
    } catch (error) {
      deferred.reject(error);
      this.#error('Sorry, something went wrong adding your bundle. Please try again.');
      setText(original);
    } finally {
      this.busy = false;
      buttons.forEach((b) => {
        b.disabled = false;
        b.removeAttribute('aria-busy');
      });
    }
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
  }

  /** Tells the cart drawer and basket count about lines added somewhere else (the pop-up) */
  async #refreshCart(action = 'update') {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines: [], promise: deferred.promise }));
    try {
      const routes = window.Theme?.routes || {};
      const ids = this.#cartSectionIds();
      const [cart, sections] = await Promise.all([
        fetch(`${routes.cart_url || '/cart'}.js`).then((r) => r.json()),
        ids.length
          ? fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((r) => r.json())
          : Promise.resolve({}),
      ]);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'bundle-builder', sourceId: this.dataset.sectionId, sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }

  /* ---------- Colour pop-up ---------- */

  /**
   * Made to order pieces have their colours, extras and prices set up in the product
   * options app, which only runs on the product page. So the pop-up shows a stripped
   * back version of that page (the product.bundle-pick template) in a frame, and we
   * watch the basket to know when they've added it.
   */
  #designerDialog() {
    if (this.designer) return this.designer;
    const dialog = document.createElement('dialog');
    dialog.className = 'll-bb__designer';
    dialog.setAttribute('aria-labelledby', `BundleDesigner-${this.dataset.sectionId}`);
    dialog.innerHTML = `
      <div class="ll-bb__designer-head">
        <div>
          <p class="ll-bb__kicker" data-designer-kicker></p>
          <p class="ll-bb__designer-title" id="BundleDesigner-${this.dataset.sectionId}" data-designer-title></p>
        </div>
        <button type="button" class="ll-bb__designer-close" data-designer-close aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
      </div>
      <p class="ll-bb__designer-note" data-designer-note hidden></p>
      <div class="ll-bb__designer-frame">
        <p class="ll-bb__designer-loading" data-designer-loading>Getting the colours ready…</p>
        <iframe title="Choose colours" data-designer-frame></iframe>
      </div>
      <p class="ll-bb__designer-foot" data-designer-foot hidden></p>`;
    dialog.querySelector('[data-designer-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    this.append(dialog);
    this.designer = dialog;
    return dialog;
  }

  /** Drops bundle pieces they've just added as extras inside the builder */
  async #coverExtras({ slot, product }) {
    const routes = window.Theme?.routes || {};
    const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { cache: 'no-store' }).then((r) => r.json());
    // Newest lines come first
    const line = cart.items.find((l) => l.product_id === product.id);
    if (!line?.properties) return;

    const chosen = Object.entries(line.properties).filter(([name, value]) => {
      if (!value || name.startsWith('_')) return false;
      // "No", "No Liner" or "No Liner, Teddy Bears" (only the second part counts)
      return String(value)
        .split(',')
        .some((part) => part.trim() && !/^no\b/i.test(part.trim()));
    });

    const covered = { ...this.state.covered };
    for (const item of this.#bundle()) {
      if (item.slot.id === slot.id || this.#isDesigned(item)) continue;
      const extra = OPTION_EXTRAS.find(
        (e) => e.title.test(item.product.title) && chosen.some(([name]) => e.option.test(name.trim()))
      );
      if (extra) covered[item.slot.id] = product.title;
    }
    this.state.covered = covered;
  }

  async #productQuantity(productId) {
    const routes = window.Theme?.routes || {};
    const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { cache: 'no-store' }).then((r) => r.json());
    return cart.items.filter((line) => line.product_id === productId).reduce((sum, line) => sum + line.quantity, 0);
  }

  /** Resolves true once the piece is in the basket, false if they close the pop-up */
  #openDesigner(item, sequence = null) {
    const { slot, product } = item;
    const dialog = this.#designerDialog();
    const frame = dialog.querySelector('[data-designer-frame]');
    const loading = dialog.querySelector('[data-designer-loading]');
    const note = dialog.querySelector('[data-designer-note]');
    const foot = dialog.querySelector('[data-designer-foot]');

    dialog.querySelector('[data-designer-kicker]').textContent =
      sequence && sequence.total > 1 ? `Handmade piece ${sequence.step} of ${sequence.total}` : 'Made to order, just for you';
    dialog.querySelector('[data-designer-title]').textContent = `Choose colours for the ${product.title}`;

    // Things already in the bundle that the builder also offers, so they don't add them twice
    const others = this.#bundle()
      .filter((i) => i.slot.id !== slot.id && /mattress|caddy|changing basket|liner|stand/i.test(i.product.title))
      .map((i) => i.product.title);
    note.hidden = !others.length;
    note.textContent = others.length
      ? `Also in your bundle: ${others.join(', ')}. If you add any of these as extras in here, we’ll take ${
          others.length > 1 ? 'them' : 'it'
        } out of your bundle so nothing doubles up.`
      : '';

    let footText = 'Add it to your basket in here and you’ll pop straight back to your bundle.';
    if (sequence && sequence.step < sequence.total) footText = 'Once it’s in your basket, we’ll show you the next one.';
    else if (sequence?.rest) footText = 'Once it’s in your basket, we’ll pop the rest of your bundle in too.';
    foot.textContent = footText;
    foot.hidden = false;

    return new Promise((resolve) => {
      let finished = false;
      let poll = 0;
      let before = null;

      const finish = (added) => {
        if (finished) return;
        finished = true;
        window.clearInterval(poll);
        dialog.removeEventListener('close', onClose);
        frame.removeEventListener('load', onLoad);
        frame.removeAttribute('srcdoc');
        if (!added) return resolve(false);
        this.state.designed = { ...this.state.designed, [slot.id]: product.id };
        dialog.close();
        this.#coverExtras(item).finally(() => {
          this.#save();
          this.#renderResults();
          this.#refreshCart('update');
          resolve(true);
        });
      };
      // The close event is queued, so one from the previous piece can land after this one opens
      const onClose = () => !dialog.open && finish(false);

      const check = async () => {
        if (before === null || finished) return;
        try {
          if ((await this.#productQuantity(product.id)) > before) finish(true);
        } catch {}
      };

      const onLoad = () => {
        if (finished || frame.contentDocument?.URL !== 'about:srcdoc') return;
        loading.hidden = true;
        // The theme's add to cart tells us straight away; the basket check below catches anything else
        try {
          frame.contentDocument?.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
            event.promise?.then((result) => !result?.detail?.didError && window.setTimeout(check, 300)).catch(() => {});
          });
        } catch {}
      };

      dialog.addEventListener('close', onClose);
      frame.addEventListener('load', onLoad);
      loading.hidden = false;

      const url = new URL(product.url, window.location.origin);
      url.searchParams.set('view', 'bundle-pick');
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      dialog.showModal();

      // Shopify won't let its pages load in a frame by address, so we fetch the page and
      // hand the frame its HTML. The base tag keeps links, scripts and the basket on this site.
      fetch(url.pathname + url.search, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          if (finished) return;
          const head = `<base href="${window.location.origin}${url.pathname}"><script>${FRAME_SHIM}</script>`;
          frame.srcdoc = html.replace(/<head[^>]*>/i, (tag) => tag + head);
        })
        .catch(() => {
          // Last resort: their own product page, same tab
          if (!finished) window.location.href = product.url;
        });

      this.#productQuantity(product.id)
        .then((quantity) => {
          before = quantity;
          poll = window.setInterval(check, 1500);
        })
        .catch(() => {
          before = 0;
          poll = window.setInterval(check, 1500);
        });
    });
  }

  /* ---------- Sign up ---------- */

  #signupHtml() {
    const s = this.state;
    if (s.signedUp) {
      const code = this.settings.signupCode
        ? `<p>Here’s a little thank you for your next order:</p><p class="ll-bb__code">${escapeHtml(this.settings.signupCode)}</p>`
        : '';
      return `<div class="ll-bb__signup" role="status">
        <h3>Thank you${s.name ? `, ${escapeHtml(s.name)}` : ''}! You’re on the list ♡</h3>
        <p class="ll-bb__small">Keep an eye on your inbox for new makes and the odd treat.</p>
        ${code}
      </div>`;
    }

    const milestone =
      this.settings.milestoneEmails && s.baby && !this.#isGift
        ? `<label class="ll-bb__consent">
            <input type="checkbox" name="milestone" value="yes">
            <span>Please remember ${escapeHtml(s.baby)}’s name and ${
              s.arrived === 'no' ? 'due month' : 'birth month'
            } so you can send the odd personal note as ${escapeHtml(this.#pronoun('they'))} grows. You can stop these any time with one click.</span>
          </label>`
        : '';

    const reward = this.settings.signupCode ? ' (and a little thank you code)' : '';
    const privacyUrl = this.dataset.privacyUrl || '/policies/privacy-policy';

    return `<form class="ll-bb__signup" data-step="signup" novalidate>
      <h3>Fancy a little hello from us now and then?</h3>
      <p class="ll-bb__small">Sign up for new makes, restocks and the odd treat${reward}.</p>
      <label class="visually-hidden" for="bb-email">Email address</label>
      <input class="ll-bb__input" id="bb-email" name="email" type="email" autocomplete="email" placeholder="Your email address" required>
      <label class="ll-bb__consent">
        <input type="checkbox" name="news" value="yes">
        <span>Yes please, send me Luna &amp; Loop news and offers by email.</span>
      </label>
      ${milestone}
      <p class="ll-bb__error" data-bb-error hidden role="alert"></p>
      <button type="submit" class="button">Sign me up</button>
      <p class="ll-bb__small">We’ll only email you if you tick the box, and you can unsubscribe any time. Read our <a href="${escapeHtml(privacyUrl)}">privacy policy</a>.</p>
    </form>`;
  }

  async #signup(form) {
    const data = new FormData(form);
    const email = String(data.get('email') || '').trim();
    const errorEl = form.querySelector('[data-bb-error]');
    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showError('Could you double check that email address?');
    if (data.get('news') !== 'yes') return showError('Just tick the box so we know it’s okay to email you.');

    const tags = ['newsletter', 'make-a-bundle'];
    if (data.get('milestone') === 'yes' && this.settings.milestoneEmails && this.state.baby) {
      const month = this.state.arrived === 'no' ? this.state.due : '';
      tags.push(`bundle-baby:${this.state.baby}`);
      if (month) tags.push(`bundle-due:${month}`);
      if (this.state.age) tags.push(`bundle-age:${this.state.age}`);
    }

    const body = new FormData();
    body.set('form_type', 'customer');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    if (this.state.name) body.set('contact[first_name]', this.state.name);
    body.set('contact[tags]', tags.join(','));

    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch('/contact', { method: 'POST', body, headers: { Accept: 'text/html' } });
      if (response.url.includes('/challenge')) {
        // Shopify wants a quick bot check, so hand over to a normal form post
        this.#postNatively(body);
        return;
      }
      this.state.signedUp = true;
      this.#save();
      this.#updatePsst();
      const fresh = document.createElement('div');
      fresh.innerHTML = this.#signupHtml();
      form.replaceWith(fresh.firstElementChild);
    } catch {
      showError('Sorry, that didn’t work. Please try again in a moment.');
      if (button) button.disabled = false;
    }
  }

  #postNatively(body) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = '/contact';
    form.hidden = true;
    for (const [name, value] of body.entries()) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      form.append(input);
    }
    document.body.append(form);
    this.state.signedUp = true;
    this.#save();
    form.submit();
  }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
